import { watch, onUnmounted } from 'vue'

/**
 * useModalBackButton
 * ==================
 * Closes the top-most open modal when the user presses the browser back button
 * (desktop), the Android hardware / gesture back button, or the iOS back
 * gesture. All of these fire a single cross-platform `popstate` event, so one
 * mechanism covers every browser, Android and iPhone.
 *
 * Public API (unchanged, per component):
 *   useModalBackButton(isOpenRef, closeFn)
 *     - isOpenRef: a Ref/computed that is truthy while the modal is open
 *     - closeFn:   a function that closes the modal (sets isOpenRef to false)
 *
 * WHY A SHARED MANAGER (and not a plain pushState/back per instance):
 * A per-instance approach breaks two common flows because history operations
 * are asynchronous and race each other:
 *   1. Modal-to-modal transitions (close A + open B in the same tick) — the
 *      closing modal's history.back() would fire *after* the opening modal's
 *      pushState and wrongly close the new modal.
 *   2. Rapid open/close.
 * This manager records the *desired* number of open modals and reconciles the
 * real history stack ONCE per microtask. A transition therefore nets zero
 * history changes; only a real net change pushes or pops an entry.
 *
 * All dummy entries share the current page URL (pushState with no url arg), so
 * Vue Router never treats them as a route change — pressing back on one simply
 * closes a modal without navigating.
 */

// --- Module-level singleton state -------------------------------------------

// Stack of currently-open modals; the last element is the top-most modal.
const stack = [] // Array<{ close: Function }>
// How many dummy history entries we currently own (sit on top of the real page).
let ownedEntries = 0
// True only while WE traverse history to clean up, so we ignore our own popstate.
let selfTraversing = false
// Microtask coalescing guard for reconcile().
let reconcileQueued = false
// The global popstate listener is attached once, lazily.
let listenerAttached = false

function attachListener() {
  if (listenerAttached) return
  window.addEventListener('popstate', onPopState)
  listenerAttached = true
}

function onPopState() {
  // Ignore the popstate caused by our own cleanup traversal.
  if (selfTraversing) {
    selfTraversing = false
    return
  }
  // A genuine back navigation. If we own entries, the browser just consumed the
  // top-most one, so close the top-most modal. If we own nothing, this is a real
  // route back — leave it for Vue Router.
  if (ownedEntries > 0 && stack.length > 0) {
    ownedEntries--
    const top = stack.pop()
    try {
      top.close()
    } catch (e) {
      /* no-op: closing must never throw out of the history handler */
    }
    // stack.length and ownedEntries were both decremented, so they stay in sync;
    // top.close() flipping the ref triggers removeModal(), which is now a no-op.
  }
}

function scheduleReconcile() {
  if (reconcileQueued) return
  reconcileQueued = true
  Promise.resolve().then(reconcile)
}

function reconcile() {
  reconcileQueued = false
  const desired = stack.length
  if (desired > ownedEntries) {
    // Need more entries (a modal opened). Push the difference.
    for (let i = ownedEntries; i < desired; i++) {
      try {
        window.history.pushState({ __modalBack: true }, '')
      } catch (e) {
        /* history API unavailable */
      }
    }
    ownedEntries = desired
  } else if (desired < ownedEntries) {
    // Fewer entries needed (a modal closed while we stay on the page). Remove the
    // surplus with a single traversal and ignore the resulting popstate.
    const toRemove = ownedEntries - desired
    ownedEntries = desired
    selfTraversing = true
    try {
      window.history.go(-toRemove)
    } catch (e) {
      selfTraversing = false
    }
  }
  // desired === ownedEntries → nothing to do (e.g. a modal-to-modal transition).
}

function addModal(entry) {
  attachListener()
  if (stack.indexOf(entry) === -1) {
    stack.push(entry)
    scheduleReconcile()
  }
}

function removeModal(entry) {
  const i = stack.indexOf(entry)
  if (i !== -1) {
    stack.splice(i, 1)
    scheduleReconcile()
  }
}

/**
 * Component teardown. Drop the entry WITHOUT traversing history: the component
 * usually unmounts because the app navigated (router.push), which already buried
 * our dummy entry — calling history.go() here would undo that navigation. We only
 * fix our own accounting so later modals stay balanced. A buried dummy entry is
 * harmless (it shares the page URL).
 */
function forgetModal(entry) {
  const i = stack.indexOf(entry)
  if (i === -1) return
  stack.splice(i, 1)
  if (ownedEntries > stack.length) {
    ownedEntries = stack.length
  }
}

// --- Composable --------------------------------------------------------------

export function useModalBackButton(isOpen, close) {
  const entry = { close }

  watch(
    isOpen,
    (open) => {
      if (open) addModal(entry)
      else removeModal(entry)
    },
    { immediate: true }
  )

  onUnmounted(() => forgetModal(entry))
}

export default useModalBackButton
